/**
 * Time formatting shared by both apps.
 *
 * Countdowns are driven by a caller-supplied `now`, never by an internal timer,
 * so the same pure functions can be unit tested and re-rendered on whatever
 * cadence each screen wants.
 *
 * Date and duration formatting is locale-driven. Where a locale has its own
 * conventions — month order, weekday names, unit suffixes — `Intl` supplies them
 * rather than a hand-written table, because a hand-written table is wrong the
 * moment a new locale is added.
 */

import { localDateKey, zonedParts } from './hours';
import { LOCALES, createTranslator, type Locale, type Translator } from './i18n';

/** Used when no translator is supplied, so pure helpers stay callable in tests. */
const englishTranslator = createTranslator('en');

function intlTag(locale: Locale): string {
  return LOCALES.find((entry) => entry.code === locale)?.intlTag ?? 'en';
}

/**
 * Formats a countdown as `MM:SS`, or `H:MM:SS` beyond an hour.
 *
 * Digits and a colon read the same in every shipped locale, so this is not
 * translated.
 *
 * @param seconds Remaining seconds; negative values are reported as overdue.
 * @param readyLabel Copy shown once the target has passed.
 * @returns A clock-style countdown.
 */
export function formatCountdown(seconds: number, readyLabel?: string): string {
  if (seconds <= 0) return readyLabel ?? englishTranslator('time.ready');

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Formats a countdown for a large display, preferring coarse units because a
 * customer reading "about 12 min" is better served than by a ticking second
 * hand.
 *
 * @param seconds Remaining seconds.
 * @param t Translator for the phrasing.
 * @returns A coarse phrase such as `about 12 min`.
 */
export function formatCountdownCoarse(seconds: number, t: Translator = englishTranslator): string {
  if (seconds <= 0) return t('time.anyMoment');
  if (seconds < 60) return t('time.underAMinute');

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('time.aboutMinutes', { count: minutes });

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? t('time.aboutHours', { hours })
    : t('time.aboutHoursMinutes', { hours, minutes: rest });
}

/**
 * Formats a duration in the locale's own unit style.
 *
 * Chinese writes `12 分 30 秒` where English writes `12m 30s`, so the whole
 * composed string lives in the dictionary rather than being assembled from
 * translated fragments.
 *
 * @param seconds Duration in seconds; nullish and negative values become the
 * "not measured" placeholder.
 * @param t Translator for the unit strings.
 * @returns A duration such as `45s`, `12m 30s`, or `2h 05m`.
 */
export function formatDuration(
  seconds: number | null | undefined,
  t: Translator = englishTranslator,
): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return t('units.notMeasured');
  }

  const total = Math.round(seconds);
  if (total < 60) return t('units.secondsOnly', { value: total });

  const minutes = Math.floor(total / 60);
  if (minutes < 60) {
    const secs = total % 60;
    return secs === 0
      ? t('units.minutesOnly', { value: minutes })
      : t('units.minutesSeconds', { minutes, seconds: String(secs).padStart(2, '0') });
  }

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? t('units.hoursOnly', { value: hours })
    : t('units.hoursMinutes', { hours, minutes: String(rest).padStart(2, '0') });
}

/**
 * Formats an instant as a wall-clock time in a specific zone.
 *
 * @param instant ISO timestamp or Date.
 * @param timeZone IANA zone name.
 * @returns `18:45`.
 */
export function formatClock(instant: string | Date, timeZone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '—';
  const p = zonedParts(date, timeZone);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/** The one wall-clock format a form field holds: no zone, no seconds. */
const ZONED_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * How far a zone is from UTC at a given instant, in milliseconds.
 *
 * Read through `Intl` rather than a stored table, so daylight saving stays the
 * zone's own business rather than ours.
 *
 * @param instant Milliseconds since the epoch.
 * @param timeZone IANA zone name.
 * @returns The offset, east positive.
 */
function zoneOffset(instant: number, timeZone: string): number {
  const at = Math.floor(instant / 1000) * 1000;
  const p = zonedParts(new Date(at), timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - at;
}

/**
 * Reads a form's `YYYY-MM-DDTHH:MM` wall time as a moment in a specific zone.
 *
 * The editor holds a wall clock, and a wall clock only means something beside a
 * zone. "18:00" on a shop's offer means six in the evening *at the shop*, so a
 * merchant editing from another country still sets the shop's evening — which is
 * what `new Date('2026-09-23T18:00')` gets wrong, because it answers with the
 * device's zone instead. This is the only place a form string becomes an instant.
 *
 * Two passes, because a zone's offset depends on the instant being asked about:
 * the first guesses with the offset at the wall time read as UTC, the second
 * re-reads the offset at that guess. Across a spring-forward gap the answer is the
 * instant the clock jumped to; across an autumn overlap it is the earlier of the
 * two, which is the one a shop means when it writes a time down.
 *
 * @param value The field's value, `YYYY-MM-DDTHH:MM`.
 * @param timeZone IANA zone name the wall time is written in.
 * @returns The instant, or null when the value is empty or malformed.
 */
export function parseZonedInput(value: string, timeZone: string): Date | null {
  const match = ZONED_INPUT.exec(value.trim());
  if (!match) return null;
  const wall = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5])
  );
  const guess = wall - zoneOffset(wall, timeZone);
  return new Date(wall - zoneOffset(guess, timeZone));
}

/**
 * Writes an instant back as the `YYYY-MM-DDTHH:MM` a form field holds.
 *
 * The inverse of {@link parseZonedInput} and in the same zone, so a value that
 * goes into a field and comes back out is the instant it started as — which is
 * what lets an editor open on a saved window without moving it.
 *
 * @param instant ISO timestamp or Date.
 * @param timeZone IANA zone name to write the wall time in.
 * @returns The field's value, or an empty string for an unreadable instant.
 */
export function formatZonedInput(instant: string | Date, timeZone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '';
  const p = zonedParts(date, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * The month and year an instant falls in, in a shop's own clock: `September 2026`.
 *
 * For facts whose day is noise — the verification mark is "we looked at this
 * restaurant then", a moment rather than an event on a calendar — and where the
 * locale's own order matters: English puts the month first, Chinese the year.
 *
 * @param instant ISO timestamp or Date.
 * @param timeZone IANA zone name.
 * @param t Translator, which also supplies the target locale.
 * @returns The month and year, or an em dash when the date cannot be read.
 */
export function formatMonthYear(
  instant: string | Date,
  timeZone: string,
  t: Translator = englishTranslator,
): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(intlTag(t.locale), {
    timeZone,
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Formats an instant as a short date and time, relative to a reference day.
 *
 * Uses `Intl` for the weekday and month names so the field order follows the
 * locale — English puts the weekday first, Chinese puts the date first.
 *
 * @param instant ISO timestamp or Date.
 * @param timeZone IANA zone name.
 * @param now Reference instant used to decide Today/Tomorrow.
 * @param t Translator, which also supplies the target locale.
 * @returns `Today 18:45`, `Tomorrow 09:15`, or a dated form.
 */
export function formatDayTime(
  instant: string | Date,
  timeZone: string,
  now: Date = new Date(),
  t: Translator = englishTranslator,
): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '—';

  const key = localDateKey(date, timeZone);
  const todayKey = localDateKey(now, timeZone);
  const clock = formatClock(date, timeZone);

  const diffDays = Math.round(
    (Date.parse(`${key}T00:00:00Z`) - Date.parse(`${todayKey}T00:00:00Z`)) / 86_400_000,
  );

  if (diffDays === 0) return `${t('common.today')} ${clock}`;
  if (diffDays === 1) return `${t('common.tomorrow')} ${clock}`;
  if (diffDays === -1) return `${t('common.yesterday')} ${clock}`;

  const dated = new Intl.DateTimeFormat(intlTag(t.locale), {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);

  return `${dated}, ${clock}`;
}

/**
 * Formats a `YYYY-MM-DD` key for an axis label.
 *
 * @param dateKey Calendar date.
 * @param style `short` gives `04 Sep`, `weekday` gives `Fri`, `month` gives `Sep`
 * — the last for an axis whose buckets are calendar months, where the day would
 * be a lie: a month's bar is the whole month, not its first day.
 * @param locale Target locale.
 * @returns A display label.
 */
export function formatDateKey(
  dateKey: string,
  style: 'short' | 'weekday' | 'month' = 'short',
  locale: Locale = 'en',
): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));

  return new Intl.DateTimeFormat(intlTag(locale), {
    timeZone: 'UTC',
    ...(style === 'weekday'
      ? { weekday: 'short' as const }
      : style === 'month'
        ? { month: 'short' as const }
        : { day: 'numeric' as const, month: 'short' as const }),
  }).format(date);
}

/**
 * Seconds between an instant and now, negated once the instant has passed.
 *
 * @param target ISO timestamp of the deadline.
 * @param now The current instant.
 * @returns Remaining seconds, possibly negative.
 */
export function secondsUntil(target: string | Date, now: Date = new Date()): number {
  const date = typeof target === 'string' ? new Date(target) : target;
  if (Number.isNaN(date.getTime())) return 0;
  return (date.getTime() - now.getTime()) / 1000;
}

/**
 * Elapsed seconds since an instant.
 *
 * @param since ISO timestamp of the start.
 * @param now The current instant, used when there is no end.
 * @param until ISO timestamp of the end. Omit to measure against `now`; a closed
 * order passes its hand-over so the readout stops there.
 * @returns Elapsed seconds, floored at zero.
 */
export function secondsSince(
  since: string | Date,
  now: Date = new Date(),
  until?: string | Date
): number {
  const date = typeof since === 'string' ? new Date(since) : since;
  if (Number.isNaN(date.getTime())) return 0;
  const end = until === undefined ? now : typeof until === 'string' ? new Date(until) : until;
  if (Number.isNaN(end.getTime())) return 0;
  return Math.max(0, (end.getTime() - date.getTime()) / 1000);
}
